import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import GreekTimeInput from './GreekTimeInput';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import TourBubble from './onboarding/TourBubble';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ShiftsPage = () => {
  const navigate = useNavigate();
  const { user, clinic, loading, isAuthenticated } = useAuth();
  const { step: onboardingStep, nextStep: onboardingNextStep, goToStep } = useOnboarding();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Shift management state
  const [shifts, setShifts] = useState([]);
  const [showNewShiftModal, setShowNewShiftModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignMainShiftModal, setShowAssignMainShiftModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [selectedVets, setSelectedVets] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState('shift'); 
  const [hourAssignments, setHourAssignments] = useState({}); 
  const [vets, setVets] = useState([]);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);
  
  const [newShiftData, setNewShiftData] = useState({
    name: '',
    fromDate: '',
    toDate: '',
    isInfinite: true,
    dayGroups: []
  });
  const [newDayGroup, setNewDayGroup] = useState({
    name: '',
    days: [],
    hours: []
  });
  const [newHour, setNewHour] = useState({
    startTime: '00:00',
    endTime: '00:00'
  });

  const weekdays = [
    { id: 1, name: 'Δευτέρα', shortName: 'Δευ' },
    { id: 2, name: 'Τρίτη', shortName: 'Τρι' },
    { id: 3, name: 'Τετάρτη', shortName: 'Τετ' },
    { id: 4, name: 'Πέμπτη', shortName: 'Πεμ' },
    { id: 5, name: 'Παρασκευή', shortName: 'Παρ' },
    { id: 6, name: 'Σάββατο', shortName: 'Σαβ' },
    { id: 7, name: 'Κυριακή', shortName: 'Κυρ' }
  ];

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (onboardingStep === 'shifts-intro') {
      goToStep('shifts-assign');
    }
  }, [onboardingStep]); 

  useEffect(() => {
    if (clinic) {
      loadExistingShifts();
      loadVets();
      loadClinicSettings();
    }
  }, [clinic]);

  useEffect(() => {
    if (clinicSettings) {
      loadHourAssignments();
    }
  }, [clinicSettings]);

  useEffect(() => {
    const handleFocus = () => {
      if (clinic) loadClinicSettings();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [clinic]);

  useEffect(() => {
    if (!clinic) return;
    const channel = supabase
      .channel('clinic-settings-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'settings_clinic_settings', filter: `clinic_id=eq.${clinic.id}` }, (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') loadClinicSettings();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'settings_clinic_working_schedules', filter: `clinic_id=eq.${clinic.id}` }, (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') loadClinicSettings();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [clinic]);

  useEffect(() => {
    return () => { if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current); };
  }, []);

  const loadExistingShifts = async () => {
    try {
      const { data: shiftsData, error: shiftsError } = await supabase
        .from('shifts')
        .select(`*, shift_day_groups (*, shift_days (*, settings_weekdays (*)), shift_hours (*))`)
        .eq('clinic_id', clinic.id)
        .order('created_at', { ascending: false });

      if (shiftsError) { showToast('Σφάλμα κατά τη φόρτωση των βαρδιών', 'error'); return; }
      if (!shiftsData || shiftsData.length === 0) { setShifts([]); return; }

      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('shift_assignments')
        .select('shift_id, vet_id')
        .in('shift_id', shiftsData.map(shift => shift.id));

      if (assignmentsError) { showToast('Σφάλμα κατά τη φόρτωση των αναθέσεων βαρδιών', 'error'); return; }

      const assignmentsByShift = {};
      assignmentsData.forEach(assignment => {
        if (!assignmentsByShift[assignment.shift_id]) assignmentsByShift[assignment.shift_id] = [];
        assignmentsByShift[assignment.shift_id].push(assignment.vet_id);
      });

      const { data: hourAssignmentsData, error: hourAssignmentsError } = await supabase
        .from('shift_hour_assignments')
        .select('shift_id, hour_id, vet_id, weekday_id')
        .in('shift_id', shiftsData.map(shift => shift.id));

      if (hourAssignmentsError) { showToast('Σφάλμα κατά τη φόρτωση των αναθέσεων ανά ώρα', 'error'); return; }

      const hourAssignmentsByShift = {};
      hourAssignmentsData.forEach(assignment => {
        if (!hourAssignmentsByShift[assignment.shift_id]) hourAssignmentsByShift[assignment.shift_id] = {};
        const key = `${assignment.weekday_id}|${assignment.hour_id}`;
        if (!hourAssignmentsByShift[assignment.shift_id][key]) hourAssignmentsByShift[assignment.shift_id][key] = [];
        hourAssignmentsByShift[assignment.shift_id][key].push(assignment.vet_id);
      });

      const transformedShifts = shiftsData.map(shift => ({
        id: shift.id, name: shift.name, fromDate: shift.from_date, toDate: shift.to_date, isActive: shift.is_active, isInfinite: shift.is_infinite,
        assignedVets: assignmentsByShift[shift.id] || [],
        hourAssignments: hourAssignmentsByShift[shift.id] || {},
        dayGroups: shift.shift_day_groups?.map(group => ({
          id: group.id, name: group.name, days: group.shift_days?.map(day => day.weekday_id) || [],
          hours: group.shift_hours?.map(hour => ({ id: hour.id, startTime: hour.start_time, endTime: hour.end_time })) || []
        })) || []
      }));
      setShifts(transformedShifts);
    } catch (error) { showToast('Σφάλμα', 'error'); }
  };

  const loadHourAssignments = async () => {
    try {
      if (!clinicSettings) return;
      const { data: mainShift } = await supabase.from('shifts').select('id').eq('clinic_id', clinic.id).eq('name', 'Κύρια Βάρδια').eq('is_active', true).single();
      if (!mainShift) return;

      const { data: existingHourAssignments } = await supabase.from('shift_hour_assignments').select('hour_id, vet_id, weekday_id').eq('shift_id', mainShift.id);
      if (existingHourAssignments) {
        const { data: mainShiftWithHours } = await supabase
          .from('shifts')
          .select(`id, shift_day_groups (id, name, shift_days (weekday_id), shift_hours (id, start_time, end_time))`)
          .eq('id', mainShift.id)
          .single();

        if (mainShiftWithHours) {
          const uiHourAssignments = {};
          existingHourAssignments.forEach(assignment => {
            const matchingHour = mainShiftWithHours.shift_day_groups?.flatMap(group => group.shift_hours || []).find(hour => hour.id === assignment.hour_id);
            if (matchingHour) {
              clinicSettings.working_schedules.forEach((schedule, scheduleIndex) => {
                schedule.days.forEach(dayId => {
                  if (dayId === assignment.weekday_id) {
                    schedule.hours.forEach((hour, hourIndex) => {
                      if (hour.start_time === matchingHour.start_time && hour.end_time === matchingHour.end_time) {
                        const uniqueHourId = createUniqueHourId(scheduleIndex, dayId, hourIndex);
                        if (!uiHourAssignments[uniqueHourId]) uiHourAssignments[uniqueHourId] = [];
                        uiHourAssignments[uniqueHourId].push(assignment.vet_id);
                      }
                    });
                  }
                });
              });
            }
          });
          setHourAssignments(uiHourAssignments);
        }
      }
    } catch (error) {}
  };

  const loadVets = async () => {
    try {
      const { data: vetsData, error: vetsError } = await supabase.from('vets').select('id, first_name, last_name, active').eq('clinic_id', clinic.id).eq('active', true).eq('deleted', false).order('first_name');
      if (vetsError) return showToast('Σφάλμα κατά τη φόρτωση των κτηνιάτρων', 'error');
      setVets(vetsData);
    } catch (error) {}
  };

  const loadClinicSettings = async () => {
    try {
      const { data: basicSettings, error: basicError } = await supabase.from('settings_clinic_settings').select('*').eq('clinic_id', clinic.id).single();
      if (basicError) { setClinicSettings(null); return; }
      const { data: schedulesData, error: schedulesError } = await supabase.from('settings_clinic_working_schedules').select(`*, settings_clinic_schedule_days (*), settings_clinic_schedule_hours (*)`).eq('clinic_id', clinic.id).order('sort_order');
      if (schedulesError) { setClinicSettings(null); return; }

      const settingsData = {
        ...basicSettings,
        working_schedules: schedulesData.map(schedule => ({
          name: schedule.group_name,
          days: schedule.settings_clinic_schedule_days?.map(day => day.weekday_id) || [],
          hours: schedule.settings_clinic_schedule_hours?.map(hour => ({ id: hour.id, start_time: hour.start_time, end_time: hour.end_time })) || []
        }))
      };
      setClinicSettings(settingsData);
    } catch (error) { setClinicSettings(null); }
  };

  const openAssignModal = async (shift) => {
    setSelectedShift(shift);
    setSelectedVets(shift.assignedVets || []);
    setAssignmentMode('shift');
    setShowAssignModal(true);

    try {
      const { data: existingHourAssignments } = await supabase.from('shift_hour_assignments').select('hour_id, vet_id, weekday_id').eq('shift_id', shift.id);
      if (existingHourAssignments) {
        const uiHourAssignments = {};
        existingHourAssignments.forEach(a => {
          const uid = createManualUniqueHourId(shift.id, a.weekday_id, a.hour_id);
          if (!uiHourAssignments[uid]) uiHourAssignments[uid] = [];
          uiHourAssignments[uid].push(a.vet_id);
        });
        setHourAssignments(uiHourAssignments);
      } else { setHourAssignments({}); }
    } catch (e) { setHourAssignments({}); }
  };

  const openAssignMainShiftModal = async () => {
    if (!clinicSettings) { showToast('Δεν υπάρχουν ρυθμίσεις για την ανάθεση της βάρδιας', 'error'); return; }
    setShowAllTeams(false);
    setSelectedVets([]);
    setAssignmentMode('shift');
    setShowAssignMainShiftModal(true);
    
    try {
      const { data: mainShift } = await supabase.from('shifts').select('id').eq('clinic_id', clinic.id).eq('name', 'Κύρια Βάρδια').single();
      if (mainShift) {
        const { data: existingAssignments } = await supabase.from('shift_assignments').select('vet_id').eq('shift_id', mainShift.id);
        if (existingAssignments) setSelectedVets(existingAssignments.map(a => a.vet_id));

        const { data: existingHourAssignments } = await supabase.from('shift_hour_assignments').select('hour_id, vet_id, weekday_id').eq('shift_id', mainShift.id);
        if (existingHourAssignments) {
          const uiHourAssignments = {};
          const { data: mainShiftWithHours } = await supabase.from('shifts').select(`id, shift_day_groups (id, name, shift_days (weekday_id), shift_hours (id, start_time, end_time))`).eq('id', mainShift.id).single();
          if (mainShiftWithHours) {
            existingHourAssignments.forEach(assignment => {
              const matchingHour = mainShiftWithHours.shift_day_groups?.flatMap(group => group.shift_hours || []).find(hour => hour.id === assignment.hour_id);
              if (matchingHour) {
                const dayGroup = mainShiftWithHours.shift_day_groups?.find(group => group.shift_hours?.some(hour => hour.id === matchingHour.id));
                if (dayGroup) {
                  clinicSettings.working_schedules.forEach((schedule, scheduleIndex) => {
                    schedule.days.forEach(dayId => {
                      if (dayId === assignment.weekday_id) {
                        schedule.hours.forEach((hour, hourIndex) => {
                          if (hour.start_time === matchingHour.start_time && hour.end_time === matchingHour.end_time) {
                            const uniqueHourId = createUniqueHourId(scheduleIndex, dayId, hourIndex);
                            if (!uiHourAssignments[uniqueHourId]) uiHourAssignments[uniqueHourId] = [];
                            uiHourAssignments[uniqueHourId].push(assignment.vet_id);
                          }
                        });
                      }
                    });
                  });
                }
              }
            });
          }
          setHourAssignments(uiHourAssignments);
        } else { setHourAssignments({}); }
      } else { setHourAssignments({}); }
    } catch (error) { setHourAssignments({}); }
  };

  const openNewShiftModal = () => {
    if (loading) return showToast('Παρακαλώ περιμένετε...', 'info');
    if (!clinic || !clinic.id) return showToast('Σφάλμα: Δεν φορτώθηκε η κλινική.', 'error');
    setNewShiftData({ name: '', fromDate: new Date().toISOString().split('T')[0], toDate: new Date().toISOString().split('T')[0], isInfinite: true, dayGroups: [] });
    setNewDayGroup({ name: '', days: [], hours: [] });
    setNewHour({ startTime: '00:00', endTime: '00:00' });
    setShowNewShiftModal(true);
  };

  const saveVetAssignments = async () => {
    if (!selectedShift || selectedVets.length === 0 || saving) return;
    setSaving(true);
    try {
      const currentlyAssigned = selectedShift.assignedVets || [];
      const vetsToAdd = selectedVets.filter(vetId => !currentlyAssigned.includes(vetId));
      const vetsToRemove = currentlyAssigned.filter(vetId => !selectedVets.includes(vetId));

      if (vetsToRemove.length > 0) await supabase.from('shift_assignments').delete().eq('shift_id', selectedShift.id).in('vet_id', vetsToRemove);
      if (vetsToAdd.length > 0) {
        const assignmentsToInsert = vetsToAdd.map(vetId => ({ shift_id: selectedShift.id, vet_id: vetId, assigned_by: user.id }));
        await supabase.from('shift_assignments').insert(assignmentsToInsert);
      }
      await loadExistingShifts();
      showToast(`Επιτυχής ανάθεση ${selectedVets.length} κτηνιάτρων`, 'success');
      setShowAssignModal(false);
      setSelectedVets([]);
    } catch (error) { showToast('Σφάλμα κατά την αποθήκευση', 'error'); } finally { setSaving(false); }
  };

  const toggleVetSelection = (vetId) => setSelectedVets(prev => prev.includes(vetId) ? prev.filter(id => id !== vetId) : [...prev, vetId]);
  const createUniqueHourId = (scheduleIndex, dayId, hourIndex) => `schedule-${scheduleIndex}-day-${dayId}-hour-${hourIndex}`;
  const createManualUniqueHourId = (shiftId, dayId, hourId) => `shift|${shiftId}|day|${dayId}|hour|${hourId}`;

  const getAssignedVetsForHour = (scheduleIndex, dayId, hourIndex) => {
    const uniqueHourId = createUniqueHourId(scheduleIndex, dayId, hourIndex);
    const assignedVetIds = hourAssignments[uniqueHourId] || [];
    return assignedVetIds.map(vetId => getVetName(vetId)).join(', ');
  };

  const toggleHourVetAssignment = (hourId, vetId) => {
    setHourAssignments(prev => {
      const currentAssignments = prev[hourId] || [];
      return currentAssignments.includes(vetId) 
        ? { ...prev, [hourId]: currentAssignments.filter(id => id !== vetId) }
        : { ...prev, [hourId]: [...currentAssignments, vetId] };
    });
  };

  const getShiftHoursByWeekday = (shift) => {
    const byDay = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
    (shift.dayGroups || []).forEach(group => {
      (group.days || []).forEach(dayId => {
        (group.hours || []).forEach(h => {
          const key = `${dayId}|${h.id}`;
          const assignedVetIds = (shift.hourAssignments && shift.hourAssignments[key]) ? shift.hourAssignments[key] : [];
          byDay[dayId].push({ id: h.id, start_time: h.startTime, end_time: h.endTime, assignedVetIds });
        });
      });
    });
    Object.keys(byDay).forEach(k => byDay[k].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    return byDay;
  };

  const saveHourAssignments = async () => {
    if (saving) return;
    setSaving(true);
    try {
      let targetShiftId = selectedShift?.id || null;
      if (!targetShiftId) {
        const { data: mainShift } = await supabase.from('shifts').select('id').eq('clinic_id', clinic.id).eq('name', 'Κύρια Βάρδια').eq('is_active', true).single();
        if (!mainShift) return showToast('Σφάλμα: Δεν βρέθηκε η κύρια βάρδια.', 'error');
        targetShiftId = mainShift.id;
      }

      const { data: shiftHoursData } = await supabase.from('shift_hours').select(`id, start_time, end_time, day_group_id, shift_day_groups!inner (shift_id, name, shift_days (weekday_id))`).eq('shift_day_groups.shift_id', targetShiftId);
      const assignmentsToUpsert = [];
      Object.entries(hourAssignments).forEach(([uniqueHourId, vetIds]) => {
        if (uniqueHourId.startsWith('schedule-')) {
          const parts = uniqueHourId.split('-');
          if (parts.length >= 6) {
            const scheduleIndex = parseInt(parts[1]), dayId = parseInt(parts[3]), hourIndex = parseInt(parts[5]);
            const schedule = clinicSettings.working_schedules[scheduleIndex];
            const settingsHour = schedule?.hours?.[hourIndex];
            if (settingsHour) {
              const matchingDbHour = shiftHoursData?.find(dbHour => {
                const dbDays = dbHour.shift_day_groups.shift_days || [];
                return dbHour.start_time === settingsHour.start_time && dbHour.end_time === settingsHour.end_time && dbDays.some(day => day.weekday_id === dayId);
              });
              if (matchingDbHour) vetIds.forEach(vetId => assignmentsToUpsert.push({ shift_id: targetShiftId, hour_id: matchingDbHour.id, vet_id: vetId, weekday_id: dayId, assigned_by: user.id }));
            }
          }
        } else if (uniqueHourId.startsWith('shift|')) {
          const parts = uniqueHourId.split('|');
          if (parts.length >= 6) {
            const dayId = parseInt(parts[3]), hourId = parts[5];
            vetIds.forEach(vetId => assignmentsToUpsert.push({ shift_id: targetShiftId, hour_id: hourId, vet_id: vetId, weekday_id: dayId, assigned_by: user.id }));
          }
        }
      });

      await supabase.from('shift_hour_assignments').delete().eq('shift_id', targetShiftId);
      if (assignmentsToUpsert.length > 0) await supabase.from('shift_hour_assignments').insert(assignmentsToUpsert);

      await loadExistingShifts();
      if (selectedShift) setShowAssignModal(false); else setShowAssignMainShiftModal(false);
      setHourAssignments({});
      setAssignmentMode('shift');
      showToast('Οι αναθέσεις αποθηκεύτηκαν επιτυχώς!', 'success');
    } catch (error) { showToast('Σφάλμα', 'error'); } finally { setSaving(false); }
  };

  const assignSelectedVetsToMainShift = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data: mainShift } = await supabase.from('shifts').select('id').eq('clinic_id', clinic.id).eq('name', 'Κύρια Βάρδια').eq('is_active', true).single();
      if (!mainShift) return showToast('Σφάλμα: Δεν βρέθηκε η κύρια βάρδια.', 'error');

      const { data: existingAssignments } = await supabase.from('shift_assignments').select('vet_id').eq('shift_id', mainShift.id);
      const existingVetIds = existingAssignments?.map(a => a.vet_id) || [];
      const vetsToAdd = selectedVets.filter(id => !existingVetIds.includes(id));
      const vetsToRemove = existingVetIds.filter(id => !selectedVets.includes(id));

      if (vetsToRemove.length > 0) await supabase.from('shift_assignments').delete().eq('shift_id', mainShift.id).in('vet_id', vetsToRemove);
      if (vetsToAdd.length > 0) await supabase.from('shift_assignments').insert(vetsToAdd.map(id => ({ shift_id: mainShift.id, vet_id: id, assigned_by: user.id })));

      await loadExistingShifts();
      setShowAssignMainShiftModal(false);
      setSelectedVets([]);
      showToast('Οι αναθέσεις ενημερώθηκαν επιτυχώς!', 'success');
    } catch (error) { showToast('Σφάλμα', 'error'); } finally { setSaving(false); }
  };

  const unassignVetFromShift = async (vetId) => {
    if (selectedShift) {
      try {
        await supabase.from('shift_assignments').delete().eq('shift_id', selectedShift.id).eq('vet_id', vetId);
        setShifts(prev => prev.map(s => s.id === selectedShift.id ? { ...s, assignedVets: (s.assignedVets || []).filter(id => id !== vetId) } : s));
        setSelectedShift(prev => ({ ...prev, assignedVets: (prev.assignedVets || []).filter(id => id !== vetId) }));
        showToast('Ο κτηνίατρος αφαιρέθηκε', 'success');
      } catch (error) { showToast('Σφάλμα', 'error'); }
    }
  };

  const getVetName = (vetId) => {
    const vet = vets.find(v => v.id === vetId);
    return vet ? `${vet.first_name} ${vet.last_name}` : 'Άγνωστος';
  };

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  // --- CHANGED FUNCTION: Smartly handle empty additions ---
  const addDayGroup = () => {
    // If they haven't selected any days...
    if (newDayGroup.days.length === 0) {
      // If they already have other groups, maybe they misclicked the button. Just silently reset and ignore.
      if (newShiftData.dayGroups && newShiftData.dayGroups.length > 0) {
        setNewHour({ startTime: '00:00', endTime: '00:00' });
        return;
      } else {
        // If they have ZERO groups in total, they need to select something.
        showToast('Παρακαλώ επιλέξτε τουλάχιστον μία ημέρα.', 'error');
        return;
      }
    }

    let allHours = [...(newDayGroup.hours || [])];
    const isDefaultTime = newHour.startTime === '00:00' && newHour.endTime === '00:00';
    
    // Only auto-push the current time if they actually touched it, OR if it's the very first time slot they are adding
    if (!isDefaultTime || allHours.length === 0) {
      allHours.push({ id: Date.now(), startTime: newHour.startTime, endTime: newHour.endTime });
    }

    if (allHours.length > 0) {
      const autoName = newDayGroup.days.sort((a, b) => a - b).map(id => weekdays.find(d => d.id === id)?.shortName).filter(Boolean).join(', ');
      setNewShiftData(prev => ({ ...prev, dayGroups: [...(prev.dayGroups || []), { id: Date.now(), name: autoName, days: [...newDayGroup.days], hours: allHours }] }));
      setNewDayGroup({ name: '', days: [], hours: [] });
      setNewHour({ startTime: '00:00', endTime: '00:00' });
    } else { 
      showToast('Συμπληρώστε τις ώρες εργασίας', 'error'); 
    }
  };

  const addHour = () => {
    if (newHour.startTime && newHour.endTime) {
      setNewDayGroup(prev => ({ ...prev, hours: [...(prev.hours || []), { id: Date.now(), startTime: newHour.startTime, endTime: newHour.endTime }] }));
      setNewHour({ startTime: '00:00', endTime: '00:00' });
    } else { showToast('Συμπληρώστε ώρες', 'error'); }
  };

  const removeDayGroup = (groupId) => setNewShiftData(prev => ({ ...prev, dayGroups: (prev.dayGroups || []).filter(g => g.id !== groupId) }));
  const removeHour = (groupId, hourId) => { if (groupId === null) setNewDayGroup(prev => ({ ...prev, hours: (prev.hours || []).filter(h => h.id !== hourId) })); };
  const toggleDaySelection = (dayId) => setNewDayGroup(prev => ({ ...prev, days: (prev.days || []).includes(dayId) ? (prev.days || []).filter(d => d !== dayId) : [...(prev.days || []), dayId] }));
  
  const toggleShiftStatus = async (shiftId) => {
    if (saving) return;
    setSaving(true);
    try {
      const shift = shifts.find(s => s.id === shiftId);
      if (!shift) return;
      await supabase.from('shifts').update({ is_active: !shift.isActive }).eq('id', shiftId);
      setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, isActive: !s.isActive } : s));
      showToast('Κατάσταση ενημερώθηκε', 'success');
    } catch (error) { showToast('Σφάλμα', 'error'); } finally { setSaving(false); }
  };

  const deleteShift = (shiftId) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;
    if (shift.name === 'Κύρια Βάρδια') return showToast('Η κύρια βάρδια δεν διαγράφεται', 'error');
    setShiftToDelete(shift);
  };

  const confirmDeleteShift = async () => {
    if (!shiftToDelete) return;
    setSaving(true);
    try {
      await supabase.from('shifts').delete().eq('id', shiftToDelete.id);
      setShifts(prev => prev.filter(s => s.id !== shiftToDelete.id));
      showToast('Η βάρδια διαγράφηκε', 'success');
    } catch (error) { showToast('Σφάλμα', 'error'); } finally { setSaving(false); setShiftToDelete(null); }
  };

  // --- CHANGED FUNCTION: Smartly handle save even with half-filled pending group ---
  const saveShift = async () => {
    if (saving) return;
    if (!clinic) return;

    let allDayGroups = [...(newShiftData.dayGroups || [])];
    
    // If there is ANY data in the pending group, try to package it nicely
    if (newDayGroup.days.length > 0) {
      let currentHours = [...(newDayGroup.hours || [])];
      const isDefaultTime = newHour.startTime === '00:00' && newHour.endTime === '00:00';
      if (!isDefaultTime || currentHours.length === 0) {
        currentHours.push({ id: Date.now(), startTime: newHour.startTime, endTime: newHour.endTime });
      }
      if (currentHours.length > 0) {
        const autoName = newDayGroup.days.sort((a,b)=>a-b).map(id => weekdays.find(d=>d.id===id)?.shortName).filter(Boolean).join(', ');
        allDayGroups.push({ id: Date.now()+1, name: autoName, days: [...newDayGroup.days], hours: currentHours });
      }
    }

    if (!newShiftData.name.trim()) return showToast('Παρακαλώ συμπληρώστε το όνομα της βάρδιας', 'error');
    
    const validDayGroups = allDayGroups.filter(g => g.days?.length > 0 && g.hours?.length > 0);
    if (validDayGroups.length === 0) return showToast('Απαιτείται τουλάχιστον μία ομάδα ημερών με ωράριο.', 'error');

    setSaving(true);
    try {
      const { data: shiftData } = await supabase.from('shifts').insert({ clinic_id: clinic.id, name: newShiftData.name, from_date: newShiftData.fromDate, to_date: newShiftData.isInfinite ? null : newShiftData.toDate, is_infinite: newShiftData.isInfinite, is_active: true }).select().single();
      if (!shiftData) return showToast('Σφάλμα', 'error');

      for (const group of validDayGroups) {
        const { data: dayGroupData } = await supabase.from('shift_day_groups').insert({ shift_id: shiftData.id, name: group.name, sort_order: 0 }).select().single();
        if (!dayGroupData) continue;
        if (group.days?.length > 0) {
          for (const day of group.days) await supabase.from('shift_days').insert({ day_group_id: dayGroupData.id, weekday_id: day });
        }
        if (group.hours?.length > 0) {
          for (const hour of group.hours) {
            if (hour.startTime && hour.endTime) await supabase.from('shift_hours').insert({ day_group_id: dayGroupData.id, start_time: hour.startTime, end_time: hour.endTime, sort_order: 0 });
          }
        }
      }
      setNewShiftData({ name: '', fromDate: new Date().toISOString().split('T')[0], toDate: new Date().toISOString().split('T')[0], isInfinite: true, dayGroups: [] });
      setShowNewShiftModal(false);
      loadExistingShifts();
      showToast('Η βάρδια δημιουργήθηκε', 'success');
    } catch (error) { showToast('Σφάλμα', 'error'); } finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Διαχείριση Βαρδιών</h1>
          <p className="text-gray-400 text-base lg:text-lg">
            Δημιουργήστε και διαχειριστείτε τις βάρδιες της κλινικής σας
          </p>
        </div>

        {!clinic ? (
          <div className="rounded-lg overflow-hidden bg-[#2D3748]">
            <div className="p-8 text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-lg font-medium text-white mb-2">Δεν βρέθηκε κλινική</h3>
              <p className="text-gray-400">Για να χρησιμοποιήσετε τη λειτουργικότητα των βαρδιών, πρέπει πρώτα να δημιουργήσετε την κλινική σας.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <button
                onClick={openNewShiftModal}
                disabled={loading || !clinic}
                className={`px-6 py-3 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${loading || !clinic ? 'bg-gray-500 cursor-not-allowed' : 'hover:bg-green-700'}`}
                style={{ backgroundColor: loading || !clinic ? '#6B7280' : '#10B981', ...(onboardingStep === 'shifts-new' ? { boxShadow: '0 0 0 3px rgba(16,185,129,0.6), 0 0 20px rgba(16,185,129,0.4)' } : {}) }}
              >
                {loading ? 'Φόρτωση...' : <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg> Νέα Βάρδια</>}
              </button>
            </div>

            <div className="space-y-8">
              {shifts.length === 0 && !clinicSettings ? (
                <div className="rounded-lg overflow-hidden bg-[#2D3748] p-8 text-center">
                  <p className="text-gray-400 text-lg mb-4">Πρώτα αποθηκεύστε τις ρυθμίσεις της κλινικής σας.</p>
                  <div className="flex justify-center gap-3">
                    <button onClick={() => navigate('/settings')} className="px-6 py-3 rounded-lg font-medium text-white bg-green-600 hover:bg-green-700 transition-colors">Πηγαίνετε στις Ρυθμίσεις</button>
                    <button onClick={loadClinicSettings} className="px-6 py-3 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">🔄 Ανανέωση</button>
                  </div>
                </div>
              ) : null}

              {/* 1. MAIN SHIFT (FROM SETTINGS) */}
              {clinicSettings && (
                <div className="rounded-xl overflow-hidden border border-gray-700 bg-[#1F2937] shadow-lg mb-8">
                  {/* Card Header */}
                  <div className="px-6 py-5 border-b border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-white">Κύρια Βάρδια</h3>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          Ενεργή
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">📅 Από τις ρυθμίσεις της κλινικής</p>

                      {/* Overall Shift Assignment Badges */}
                      {(() => {
                        const mainShift = shifts.find(shift => shift.name === 'Κύρια Βάρδια');
                        const shiftAssigned = mainShift?.assignedVets || [];
                        if (shiftAssigned.length > 0) {
                          return (
                            <div className="mt-3 flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-400">Συνολική Ανάθεση:</span>
                              <div className="flex flex-wrap gap-1.5">
                                {shiftAssigned.map((vetId) => (
                                  <span key={vetId} className="flex items-center gap-1.5 bg-[#374151] border border-gray-600 text-gray-200 text-xs px-2 py-1 rounded-md">
                                    <div className="w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold">
                                      {getVetName(vetId).charAt(0)}
                                    </div>
                                    {getVetName(vetId)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openAssignMainShiftModal()}
                        className="px-4 py-2 bg-transparent border border-gray-600 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Ανάθεση
                      </button>
                    </div>
                  </div>

                  {/* Calendar Grid */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                      {weekdays.map((day) => {
                        const scheduleGroup = clinicSettings.working_schedules?.find(schedule => schedule.days?.includes(day.id));
                        const isWorkingDay = scheduleGroup && scheduleGroup.hours && scheduleGroup.hours.length > 0;

                        return (
                          <div key={day.id} className="flex flex-col h-full">
                            <div className="text-center mb-3">
                              <span className="text-sm font-semibold text-gray-400">{day.name}</span>
                            </div>
                            <div className={`flex-1 rounded-xl border p-2 ${isWorkingDay ? 'bg-[#111827] border-gray-700' : 'bg-[#111827]/50 border-gray-800'}`}>
                              {isWorkingDay ? (
                                <div className="space-y-2">
                                  {scheduleGroup.hours.map((hour, hourIndex) => {
                                    const scheduleIndex = clinicSettings.working_schedules?.findIndex(s => s.days?.includes(day.id)) || 0;
                                    const assignedVets = getAssignedVetsForHour(scheduleIndex, day.id, hourIndex);
                                    
                                    return (
                                      <div key={hourIndex} className="bg-[#374151] border border-gray-600 rounded-lg p-2.5 flex flex-col items-center justify-center transition-colors hover:bg-gray-600">
                                        <span className="text-xs font-medium text-white mb-1.5">
                                          {hour.start_time.substring(0, 5)} - {hour.end_time.substring(0, 5)}
                                        </span>
                                        {assignedVets && (
                                          <div className="flex flex-wrap justify-center gap-1 mt-1">
                                            {assignedVets.split(',').map((v, i) => v.trim() && (
                                              <span key={i} className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded-full border border-blue-500/20">
                                                {v.trim()}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center h-24 opacity-40">
                                  <svg className="w-5 h-5 text-gray-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                  <div className="text-xs text-gray-500 font-medium">Κλειστό</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* 2. OTHER SHIFTS */}
              {shifts.filter((s) => s.name !== 'Κύρια Βάρδια').map((shift) => {
                const hoursByDay = getShiftHoursByWeekday(shift);
                return (
                  <div key={shift.id} className="rounded-xl overflow-hidden border border-gray-700 bg-[#1F2937] shadow-lg mb-8">
                    {/* Card Header */}
                    <div className="px-6 py-5 border-b border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xl font-bold text-white">{shift.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${shift.isActive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                            {shift.isActive ? 'Ενεργή' : 'Ανενεργή'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">
                          Από: {shift.fromDate} - Έως: {shift.isInfinite ? 'Απεριόριστη διάρκεια' : shift.toDate}
                        </p>

                        {/* Overall Shift Assignment Badges */}
                        {shift.assignedVets && shift.assignedVets.length > 0 && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-400">Συνολική Ανάθεση:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {shift.assignedVets.map((vetId) => (
                                <span key={vetId} className="flex items-center gap-1.5 bg-[#374151] border border-gray-600 text-gray-200 text-xs px-2 py-1 rounded-md">
                                  <div className="w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold">
                                    {getVetName(vetId).charAt(0)}
                                  </div>
                                  {getVetName(vetId)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => openAssignModal(shift)} className="px-4 py-2 bg-transparent border border-gray-600 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          Ανάθεση
                        </button>
                        <button onClick={() => toggleShiftStatus(shift.id)} className="px-4 py-2 bg-transparent border border-gray-600 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors">
                          {shift.isActive ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                        </button>
                        <button onClick={() => deleteShift(shift.id)} className="px-3 py-2 bg-transparent border border-red-900/50 hover:bg-red-900/30 text-red-400 rounded-lg transition-colors" title="Διαγραφή βάρδιας">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>

                    {/* Calendar Grid */}
                    <div className="p-6">
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                        {weekdays.map((day) => {
                          const dayHours = hoursByDay[day.id] || [];
                          const isWorkingDay = dayHours.length > 0;
                          return (
                            <div key={day.id} className="flex flex-col h-full">
                              <div className="text-center mb-3">
                                <span className="text-sm font-semibold text-gray-400">{day.name}</span>
                              </div>
                              <div className={`flex-1 rounded-xl border p-2 ${isWorkingDay ? 'bg-[#111827] border-gray-700' : 'bg-[#111827]/50 border-gray-800'}`}>
                                {isWorkingDay ? (
                                  <div className="space-y-2">
                                    {dayHours.map((hour, idx) => (
                                      <div key={`${day.id}-${hour.id}-${idx}`} className="bg-[#374151] border border-gray-600 rounded-lg p-2.5 flex flex-col items-center justify-center transition-colors hover:bg-gray-600">
                                        <span className="text-xs font-medium text-white mb-1.5">
                                          {hour.start_time.substring(0, 5)} - {hour.end_time.substring(0, 5)}
                                        </span>
                                        {hour.assignedVetIds && hour.assignedVetIds.length > 0 && (
                                          <div className="flex flex-wrap justify-center gap-1 mt-1">
                                            {hour.assignedVetIds.map(vetId => (
                                              <span key={vetId} className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded-full border border-blue-500/20">
                                                {getVetName(vetId)}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center h-24 opacity-40">
                                    <svg className="w-5 h-5 text-gray-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                    <div className="text-xs text-gray-500 font-medium">Κλειστό</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* --- ALL MODALS REMAIN EXACTLY THE SAME BELOW THIS LINE --- */}

      {/* New Shift Modal */}
      {showNewShiftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={() => setShowNewShiftModal(false)}>
          <div className="rounded-xl border max-w-4xl w-full max-h-[85vh] flex flex-col shadow-2xl bg-[#1F2937] border-gray-700" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex-shrink-0 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Νέα Βάρδια</h3>
              <button onClick={() => setShowNewShiftModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-6">
                {/* Basic Shift Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Όνομα Βάρδιας</label>
                    <input
                      type="text"
                      value={newShiftData.name}
                      onChange={(e) => setNewShiftData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-[#111827] border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-white"
                      placeholder="π.χ. Κεντρική Βάρδια"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Ημερομηνία Έναρξης</label>
                    <DatePicker
                      selected={newShiftData.fromDate ? new Date(newShiftData.fromDate + 'T00:00:00') : null}
                      onChange={(date) => {
                        if (date) {
                          const yyyy = date.getFullYear();
                          const mm = String(date.getMonth() + 1).padStart(2, '0');
                          const dd = String(date.getDate()).padStart(2, '0');
                          setNewShiftData(prev => ({ ...prev, fromDate: `${yyyy}-${mm}-${dd}` }));
                        } else {
                          setNewShiftData(prev => ({ ...prev, fromDate: '' }));
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Επιλέξτε ημερομηνία"
                      className="w-full px-3 py-2 bg-[#111827] border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-white"
                      wrapperClassName="w-full"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer group">
                    <div className="relative flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={newShiftData.isInfinite}
                        onChange={(e) => setNewShiftData(prev => ({ ...prev, isInfinite: e.target.checked }))}
                        className="peer appearance-none w-5 h-5 border border-gray-500 rounded bg-[#111827] checked:bg-emerald-500 checked:border-emerald-500 transition-all"
                      />
                      <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="ml-2 text-sm text-gray-300 group-hover:text-white transition-colors">Απεριόριστη διάρκεια</span>
                  </label>
                </div>

                {!newShiftData.isInfinite && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Ημερομηνία Λήξης</label>
                    <DatePicker
                      selected={newShiftData.toDate ? new Date(newShiftData.toDate + 'T00:00:00') : null}
                      onChange={(date) => {
                        if (date) {
                          const yyyy = date.getFullYear();
                          const mm = String(date.getMonth() + 1).padStart(2, '0');
                          const dd = String(date.getDate()).padStart(2, '0');
                          setNewShiftData(prev => ({ ...prev, toDate: `${yyyy}-${mm}-${dd}` }));
                        } else {
                          setNewShiftData(prev => ({ ...prev, toDate: '' }));
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Επιλέξτε ημερομηνία"
                      className="w-full px-3 py-2 bg-[#111827] border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-white"
                      wrapperClassName="w-full"
                    />
                  </div>
                )}

                {/* Day Groups Section */}
                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-lg font-medium text-white mb-4">Ομάδες Ημερών & Ώρες</h4>
                  
                  {/* Existing Day Groups */}
                  {(newShiftData.dayGroups || []).map((group) => (
                    <div key={group.id} className="p-4 rounded-xl border border-gray-700 bg-[#111827] mb-4">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="font-semibold text-white">{group.name}</h5>
                        <button onClick={() => removeDayGroup(group.id)} className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                      
                      <div className="flex gap-6">
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">ΗΜΕΡΕΣ</div>
                          <div className="flex flex-wrap gap-2">
                            {(group.days || []).map((dayId) => {
                              const day = weekdays.find(d => d.id === dayId);
                              return day ? <span key={dayId} className="px-3 py-1 bg-[#1F2937] border border-gray-600 text-gray-300 rounded-full text-sm font-medium">{day.shortName}</span> : null;
                            })}
                          </div>
                        </div>
                        <div className="flex-1 border-l border-gray-700 pl-6">
                          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">ΩΡΑΡΙΑ</div>
                          <div className="flex flex-wrap gap-2">
                            {(group.hours || []).map((hour) => (
                              <span key={hour.id} className="px-3 py-1 bg-[#1F2937] border border-gray-600 text-gray-300 rounded-full text-sm font-medium">
                                {hour.startTime.substring(0, 5)} - {hour.endTime.substring(0, 5)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add New Day Group Form */}
                  <div className="p-5 rounded-xl border border-gray-700 bg-[#374151]/30">
                    <h5 className="font-medium text-white mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Δημιουργία Νέας Ομάδας
                    </h5>

                    {/* Days Selection */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-400 mb-2">1. Επιλέξτε Ημέρες</label>
                      <div className="flex flex-wrap gap-2">
                        {weekdays.map((day) => {
                          const isSelected = (newDayGroup.days || []).includes(day.id);
                          return (
                            <button
                              key={day.id}
                              onClick={() => toggleDaySelection(day.id)}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                isSelected
                                  ? 'bg-blue-600 text-white border border-blue-500 shadow-md shadow-blue-900/20'
                                  : 'bg-[#111827] text-gray-400 border border-gray-700 hover:border-gray-500 hover:text-gray-200'
                              }`}
                            >
                              {day.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Hours Section */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-400 mb-2">2. Καθορίστε Ωράρια</label>
                      
                      {/* Existing Hours */}
                      {(newDayGroup.hours || []).map((hour) => (
                        <div key={hour.id} className="flex items-center gap-3 mb-3 p-3 bg-[#111827] border border-gray-700 rounded-lg w-fit">
                          <GreekTimeInput
                            value={hour.startTime}
                            onChange={(newTime) => {
                              const updatedHours = (newDayGroup.hours || []).map(h => h.id === hour.id ? { ...h, startTime: newTime } : h);
                              setNewDayGroup(prev => ({ ...prev, hours: updatedHours }));
                            }}
                          />
                          <span className="text-gray-500 font-medium px-2">έως</span>
                          <GreekTimeInput
                            value={hour.endTime}
                            onChange={(newTime) => {
                              const updatedHours = (newDayGroup.hours || []).map(h => h.id === hour.id ? { ...h, endTime: newTime } : h);
                              setNewDayGroup(prev => ({ ...prev, hours: updatedHours }));
                            }}
                          />
                          <button onClick={() => removeHour(null, hour.id)} className="ml-2 p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      ))}

                      {/* Add New Hour Input */}
                      <div className="flex items-center gap-3 p-3 bg-[#111827] border border-gray-700 rounded-lg w-fit">
                        <GreekTimeInput value={newHour.startTime} onChange={(newTime) => setNewHour(prev => ({ ...prev, startTime: newTime }))} />
                        <span className="text-gray-500 font-medium px-2">έως</span>
                        <GreekTimeInput value={newHour.endTime} onChange={(newTime) => setNewHour(prev => ({ ...prev, endTime: newTime }))} />
                        <button
                          onClick={addHour}
                          className="ml-2 px-3 py-1.5 bg-[#374151] hover:bg-gray-600 text-gray-200 border border-gray-600 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          Προσθήκη
                        </button>
                      </div>
                    </div>

                    {/* Save Day Group Button & Clear Button */}
                    <div className="flex gap-3">
                      <button
                        onClick={addDayGroup}
                        className="flex-1 py-3 rounded-xl border-2 border-dashed border-gray-600 text-gray-400 hover:text-white hover:border-gray-400 hover:bg-[#374151]/50 font-medium transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Καταχώρηση Ομάδας στη Βάρδια
                      </button>
                      
                      {/* Show clear button if there's any data in pending form */}
                      {(newDayGroup.days.length > 0 || newDayGroup.hours.length > 0 || newHour.startTime !== '00:00' || newHour.endTime !== '00:00') && (
                        <button
                          onClick={() => {
                            setNewDayGroup({ name: '', days: [], hours: [] });
                            setNewHour({ startTime: '00:00', endTime: '00:00' });
                          }}
                          className="px-4 py-3 rounded-xl border border-gray-700 bg-[#1F2937] text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Καθαρισμός"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 flex gap-3 bg-[#111827] rounded-b-xl">
              <button
                onClick={saveShift}
                disabled={saving}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ background: 'linear-gradient(90deg, #1053b9, #1675b4)' }}
              >
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση Βάρδιας'}
              </button>
              <button
                onClick={() => setShowNewShiftModal(false)}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-colors bg-[#374151] hover:bg-gray-600 border border-gray-600"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Shift Modal (manual shifts) */}
      {showAssignModal && selectedShift && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAssignModal(false)}>
          <div className="rounded-xl border border-gray-700 bg-[#1F2937] max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Ανάθεση: {selectedShift.name}</h3>
                <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              
              {/* Segmented Control */}
              <div className="flex p-1 bg-[#111827] rounded-lg mt-5 border border-gray-800">
                <button
                  onClick={() => setAssignmentMode('shift')}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                    assignmentMode === 'shift' ? 'bg-[#374151] text-white shadow shadow-black/20' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Συνολική Ανάθεση
                </button>
                <button
                  onClick={() => setAssignmentMode('hour')}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                    assignmentMode === 'hour' ? 'bg-[#374151] text-white shadow shadow-black/20' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Ανάθεση ανά Ημέρα / Ώρα
                </button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-6">
                {assignmentMode === 'shift' ? (
                  <>
                    <p className="text-sm text-gray-400">Επιλέξτε τους κτηνιάτρους που θα έχουν την ευθύνη για <strong>ολόκληρη</strong> αυτή τη βάρδια.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {vets.length === 0 ? (
                        <div className="col-span-2 text-center py-8">
                          <p className="text-gray-400">Δεν υπάρχουν διαθέσιμοι κτηνίατροι</p>
                        </div>
                      ) : (
                        vets.map((vet) => {
                          const isSelected = selectedVets.includes(vet.id);
                          return (
                            <div 
                              key={vet.id} 
                              onClick={() => toggleVetSelection(vet.id)}
                              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
                                isSelected 
                                  ? 'bg-blue-500/10 border-blue-500/50 shadow-sm' 
                                  : 'bg-[#111827] border-gray-700 hover:border-gray-500'
                              }`} 
                            >
                              <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-gray-800 border-gray-600'}`}>
                                {isSelected && <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                              </div>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
                                {vet.first_name.charAt(0)}{vet.last_name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-gray-300'}`}>{vet.first_name} {vet.last_name}</div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-400">Πατήστε πάνω στα ονόματα των κτηνιάτρων για να τους αναθέσετε στο συγκεκριμένο ωράριο.</p>
                    {(selectedShift.dayGroups || []).map((group) => (
                      <div key={group.id} className="space-y-4 border border-gray-700 rounded-xl p-4 bg-[#111827]">
                        <div className="text-sm font-semibold text-white border-b border-gray-700 pb-2">{group.name}</div>
                        <div className="space-y-4">
                          {(group.days || []).map(dayId => {
                            const day = weekdays.find(d => d.id === dayId);
                            return (
                              <div key={dayId} className="flex flex-col gap-2">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{day?.name}</div>
                                <div className="space-y-2">
                                  {(group.hours || []).map((hour) => {
                                    const uniqueHourId = createManualUniqueHourId(selectedShift.id, dayId, hour.id);
                                    return (
                                      <div key={`${uniqueHourId}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg bg-[#1F2937] border border-gray-700">
                                        <div className="text-sm text-white font-medium whitespace-nowrap bg-[#374151] px-2 py-1 rounded-md w-fit">
                                          {hour.startTime.substring(0,5)} - {hour.endTime.substring(0,5)}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {vets.map(vet => {
                                            const isAssigned = (hourAssignments[uniqueHourId] || []).includes(vet.id);
                                            return (
                                              <button
                                                key={`${uniqueHourId}-${vet.id}`}
                                                onClick={() => toggleHourVetAssignment(uniqueHourId, vet.id)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                                                  isAssigned
                                                    ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-900/20'
                                                    : 'bg-[#111827] text-gray-400 border-gray-600 hover:border-gray-400 hover:text-gray-200'
                                                }`}
                                              >
                                                {isAssigned && <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                                {vet.first_name}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 flex gap-3 bg-[#111827] rounded-b-xl">
              <button
                onClick={assignmentMode === 'shift' ? saveVetAssignments : saveHourAssignments}
                disabled={saving || (assignmentMode === 'shift' && selectedVets.length === 0)}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-colors bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {saving ? 'Αποθήκευση...' : assignmentMode === 'shift' ? `Αποθήκευση (${selectedVets.length})` : 'Αποθήκευση Αναθέσεων'}
              </button>
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-colors bg-[#374151] hover:bg-gray-600 border border-gray-600"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Main Shift Modal */}
      {showAssignMainShiftModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowAssignMainShiftModal(false); setAssignmentMode('shift'); setHourAssignments({}); }}>
          <div className="rounded-xl border border-gray-700 bg-[#1F2937] max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Ανάθεση: Κύρια Βάρδια</h3>
                <button
                  onClick={() => { setShowAssignMainShiftModal(false); setAssignmentMode('shift'); setHourAssignments({}); }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              
              {/* Segmented Control */}
              <div className="flex p-1 bg-[#111827] rounded-lg mt-5 border border-gray-800">
                <button
                  onClick={() => setAssignmentMode('shift')}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                    assignmentMode === 'shift' ? 'bg-[#374151] text-white shadow shadow-black/20' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Συνολική Ανάθεση
                </button>
                <button
                  onClick={() => setAssignmentMode('hour')}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                    assignmentMode === 'hour' ? 'bg-[#374151] text-white shadow shadow-black/20' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Ανάθεση ανά Ημέρα / Ώρα
                </button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-6">
                
                {assignmentMode === 'shift' ? (
                  <>
                    <p className="text-sm text-gray-400">Επιλέξτε τους κτηνιάτρους που θα έχουν την ευθύνη για <strong>ολόκληρη</strong> την Κύρια Βάρδια της κλινικής.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {vets.length === 0 ? (
                        <div className="col-span-2 text-center py-8">
                          <p className="text-gray-400">Δεν υπάρχουν διαθέσιμοι κτηνίατροι</p>
                        </div>
                      ) : (
                        vets.map((vet) => {
                          const isSelected = selectedVets.includes(vet.id);
                          return (
                            <div 
                              key={vet.id} 
                              onClick={() => toggleVetSelection(vet.id)}
                              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
                                isSelected 
                                  ? 'bg-blue-500/10 border-blue-500/50 shadow-sm' 
                                  : 'bg-[#111827] border-gray-700 hover:border-gray-500'
                              }`} 
                            >
                              <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-gray-800 border-gray-600'}`}>
                                {isSelected && <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                              </div>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
                                {vet.first_name.charAt(0)}{vet.last_name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-gray-300'}`}>{vet.first_name} {vet.last_name}</div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-400">Πατήστε πάνω στα ονόματα των κτηνιάτρων για να τους αναθέσετε στο συγκεκριμένο ωράριο.</p>
                    {(clinicSettings.working_schedules || []).map((schedule, scheduleIndex) => (
                      <div key={scheduleIndex} className="space-y-4 border border-gray-700 rounded-xl p-4 bg-[#111827]">
                        <div className="text-sm font-semibold text-white border-b border-gray-700 pb-2">
                          {schedule.name || `Ομάδα ${scheduleIndex + 1}`}
                        </div>
                        <div className="space-y-4">
                          {(schedule.days || []).map(dayId => {
                            const day = weekdays.find(d => d.id === dayId);
                            return (
                              <div key={dayId} className="flex flex-col gap-2">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{day?.name}</div>
                                <div className="space-y-2">
                                  {(schedule.hours || []).map((hour, hourIndex) => {
                                    const uniqueHourId = createUniqueHourId(scheduleIndex, dayId, hourIndex);
                                    return (
                                      <div key={uniqueHourId} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg bg-[#1F2937] border border-gray-700">
                                        <div className="text-sm text-white font-medium whitespace-nowrap bg-[#374151] px-2 py-1 rounded-md w-fit">
                                          {hour.start_time.substring(0, 5)} - {hour.end_time.substring(0, 5)}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {vets.map((vet) => {
                                            const isAssigned = (hourAssignments[uniqueHourId] || []).includes(vet.id);
                                            return (
                                              <button
                                                key={`${uniqueHourId}-${vet.id}`}
                                                onClick={() => toggleHourVetAssignment(uniqueHourId, vet.id)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                                                  isAssigned
                                                    ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-900/20'
                                                    : 'bg-[#111827] text-gray-400 border-gray-600 hover:border-gray-400 hover:text-gray-200'
                                                }`}
                                              >
                                                {isAssigned && <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                                {vet.first_name}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 flex gap-3 bg-[#111827] rounded-b-xl">
              <button
                onClick={assignmentMode === 'shift' ? assignSelectedVetsToMainShift : saveHourAssignments}
                disabled={saving}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-colors bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {saving ? 'Αποθήκευση...' : assignmentMode === 'shift'
                  ? `Αποθήκευση (${selectedVets.length})`
                  : 'Αποθήκευση Αναθέσεων'
                }
              </button>
              <button
                onClick={() => { setShowAssignMainShiftModal(false); setAssignmentMode('shift'); setHourAssignments({}); }}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-colors bg-[#374151] hover:bg-gray-600 border border-gray-600"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {shiftToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShiftToDelete(null)}>
          <div className="rounded-xl border max-w-sm w-full shadow-2xl bg-[#1F2937] border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-5 bg-red-500/10 border border-red-500/20">
                <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Διαγραφή Βάρδιας</h3>
              <p className="text-gray-400 text-sm">
                Είστε σίγουροι ότι θέλετε να διαγράψετε τη βάρδια <span className="text-white font-medium">"{shiftToDelete.name}"</span>; Η ενέργεια αυτή δεν μπορεί να αναιρεθεί.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 flex gap-3 bg-[#111827] rounded-b-xl">
              <button
                onClick={confirmDeleteShift}
                disabled={saving}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-colors bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {saving ? 'Διαγραφή...' : 'Διαγραφή'}
              </button>
              <button
                onClick={() => setShiftToDelete(null)}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-colors bg-[#374151] hover:bg-gray-600 border border-gray-600"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
              boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
              }`
            }}
          >
            {toast.type === 'success' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(16,185,129,0.2)' }}>
                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {toast.type === 'error' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
            {toast.type === 'info' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(59,130,246,0.2)' }}>
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
            <span className="text-white font-medium flex-1">{toast.message}</span>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-3 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftsPage;